import { HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const WASM_BASE = '/wasm';
const MODEL_URL = '/models/hand_landmarker.task';

const MODEL_FALLBACKS: string[] = [
  'https://huggingface.co/qualcomm/MediaPipe-Hand-Landmarker/resolve/main/hand_landmarker.task',
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
];

export type HandLandmark = { x: number; y: number; z: number };
export type HandData = {
  landmarks: HandLandmark[][];
  handedness: { categoryName: string; score: number }[];
  timestamp: number;
};

export type HandDetectorStatus = {
  modelLoaded: boolean;
  modelError: string | null;
  lastError: string | null;
  lastHandTime: number;
};

export class HandDetector {
  private landmarker: HandLandmarker | null = null;
  private status: HandDetectorStatus = {
    modelLoaded: false,
    modelError: null,
    lastError: null,
    lastHandTime: 0,
  };

  getStatus(): HandDetectorStatus {
    return this.status;
  }

  async load(): Promise<void> {
    const filesetResolver = await FilesetResolver.forVisionTasks(WASM_BASE);

    const urlsToTry = [MODEL_URL, ...MODEL_FALLBACKS];
    let lastErr: unknown = null;

    for (const url of urlsToTry) {
      try {
        this.landmarker = await HandLandmarker.createFromOptions(filesetResolver, {
          baseOptions: { modelAssetPath: url, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numHands: 1,
        });
        this.status = {
          modelLoaded: true,
          modelError: null,
          lastError: null,
          lastHandTime: 0,
        };
        console.info(`[hand] model loaded from ${url}`);
        return;
      } catch (err) {
        lastErr = err;
        console.warn(`[hand] failed to load model from ${url}:`, err);
      }
    }

    const errMsg = lastErr instanceof Error ? lastErr.message : String(lastErr);
    this.status = {
      modelLoaded: false,
      modelError: errMsg,
      lastError: errMsg,
      lastHandTime: 0,
    };
    throw new Error(`无法加载手部识别模型：${errMsg}`);
  }

  detect(video: HTMLVideoElement, now: number): HandData | null {
    if (!this.landmarker || video.readyState < 2) return null;
    try {
      const result = this.landmarker.detectForVideo(video, now);
      if (result.landmarks.length === 0) return null;
      this.status.lastHandTime = now;
      this.status.lastError = null;
      return {
        landmarks: result.landmarks as HandLandmark[][],
        handedness: (result.handedness ?? []).map(
          (cats) =>
            (cats[0] && {
              categoryName: cats[0].categoryName ?? 'Unknown',
              score: cats[0].score ?? 0,
            }) ?? { categoryName: 'Unknown', score: 0 }
        ),
        timestamp: now,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.status.lastError = msg;
      return null;
    }
  }

  getLastHandTime(): number {
    return this.status.lastHandTime;
  }

  isReady(): boolean {
    return this.landmarker !== null;
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
    this.status.modelLoaded = false;
  }
}
